import {
  Alert,
  Button,
  LoadingOverlay,
  Paper,
  Stack,
  Text,
} from '@mantine/core';
import { ServiceRequest } from '@medplum/fhirtypes';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useDiagnosticReportsList } from '../hooks/useDiagnosticReportList';
import { useReviewTasks } from '../hooks/useReviewTasks';
import { ARKON } from '@/consts/systems.consts';
import { IconFileText, IconFileTypography } from '@tabler/icons-react';
import { ROUTES } from '@/consts/routes.consts';
import { useNavigate } from 'react-router-dom';

interface ReportBoxProps {
  serviceRequest: ServiceRequest | undefined;
}

export const ReportBox: FC<ReportBoxProps> = ({ serviceRequest }) => {
  const { t } = useTranslation('lims');
  const navigate = useNavigate();

  const diagnosticReports = useDiagnosticReportsList({
    serviceRequestId: serviceRequest?.id,
  });
  const { latestReport } = diagnosticReports;
  const { tasks: pendingReviewTasks, loading: reviewTasksLoading } =
    useReviewTasks({ diagnosticReportId: latestReport?.id });

  // Report status flags
  const hasPendingReviews = pendingReviewTasks.length > 0;
  const hasPreliminaryReport = latestReport?.status === 'preliminary';
  const hasFinalReport = latestReport?.status === 'final';
  const hasRegisteredReport = latestReport?.status === 'registered';
  const hasReviewableReport = hasPreliminaryReport || hasFinalReport;

  // ---------------------------------------------------------------------------
  const getReviewStatusDisplay = () => {
    if (!latestReport) {
      return null;
    }

    const reviewExtension = latestReport.extension?.find(
      (ext) => ext.url === ARKON.SD.DIAGNOSTIC_REPORT.REVIEW_STATUS
    );

    if (reviewExtension?.extension) {
      const getExtValue = (url: string) => {
        const ext = reviewExtension.extension?.find((e) => e.url === url);
        return ext?.valueBoolean ?? ext?.valueInteger ?? ext?.valueString;
      };

      const totalTasks = (getExtValue('totalReviewers') as number) || 0;
      const approvedTasks = (getExtValue('approvedCount') as number) || 0;
      const allApproved = (getExtValue('allApproved') as boolean) || false;

      return { totalTasks, approvedTasks, allApproved };
    }

    return null;
  };

  // ---------------------------------------------------------------------------
  const reviewStatus = getReviewStatusDisplay();

  return (
    <Paper shadow="sm" p="md" withBorder flex="1" miw="350px">
      <Stack gap="md">
        {reviewTasksLoading && <LoadingOverlay visible />}{' '}
        <Text size="sm" fw={600}>
          {hasFinalReport
            ? t('orderDetails.reportCompleted', { ns: 'lims' })
            : hasPreliminaryReport
              ? t('orderDetails.inReview', { ns: 'lims' })
              : hasRegisteredReport
                ? t('orderDetails.draftAvailable', { ns: 'lims' })
                : t('reportEditor.title', { ns: 'lims' })}
        </Text>
        {/* Show status for preliminary reports */}
        {hasPreliminaryReport && (
          <Alert color={reviewStatus?.allApproved ? 'green' : 'blue'}>
            <Text size="sm" fw={600}>
              {reviewStatus?.allApproved
                ? t('orderDetails.allApproved', { ns: 'lims' })
                : t('orderDetails.reviewInProgress', { ns: 'lims' })}
            </Text>
            <Text size="sm">
              {reviewStatus?.allApproved
                ? t('orderDetails.allApprovedDescription', {
                    ns: 'lims',
                  })
                : reviewStatus && reviewStatus.totalTasks > 0
                  ? t('orderDetails.reviewProgress', {
                      ns: 'lims',
                      approved: reviewStatus.approvedTasks,
                      total: reviewStatus.totalTasks,
                    })
                  : t('orderDetails.awaitingApproval', {
                      ns: 'lims',
                      pending: hasPendingReviews
                        ? pendingReviewTasks.length
                        : 0,
                    })}
            </Text>
          </Alert>
        )}
        {/* Show status for final reports */}
        {hasFinalReport && (
          <Alert color="green">
            <Text size="sm">
              {t('orderDetails.reportCompletedAvailable', {
                ns: 'lims',
              })}
            </Text>
          </Alert>
        )}
        {hasReviewableReport ? (
          <Button
            leftSection={<IconFileText size={16} />}
            variant="outline"
            onClick={() => {
              if (latestReport?.id && serviceRequest?.id) {
                navigate(
                  ROUTES.LIMS.DIAGNOSTIC_REPORT(
                    serviceRequest.id,
                    latestReport.id
                  )
                );
              }
            }}
          >
            {t('orderDetails.viewReport', { ns: 'lims' })}
          </Button>
        ) : (
          <Button
            variant="light"
            color="blue"
            leftSection={<IconFileTypography size={16} />}
            onClick={() => {
              if (hasRegisteredReport && latestReport?.id) {
                navigate(
                  ROUTES.LIMS.EDIT_REPORT_EXISTING(
                    serviceRequest?.id || '',
                    latestReport.id
                  )
                );
              } else {
                navigate(ROUTES.LIMS.EDIT_REPORT(serviceRequest?.id || ''));
              }
            }}
          >
            {t('orderDetails.openRichTextEditor', { ns: 'lims' })}
          </Button>
        )}
      </Stack>
    </Paper>
  );
};
